import { useGetProteinBalance } from "@/hooks/requests/balance/use-get-protein-balance";
import { useGetPlan } from "@/hooks/requests/plan/use-get-plan";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { IonSkeletonText } from "@ionic/react";
import { format } from "date-fns";
import { memo, useEffect, useMemo, useState } from "react";
import { PlanStatusCheckIcon } from "../plan-status-circle/plan-status-check-icon";
import { getLoggedProteinSummary, getProteinStatus, ProteinStatus } from "./protein-status";

// ─── Static nutrient data config ─────────────────────────────────────────────
// Fibre/sugar are still mocked pending server data; update their
// progressPercent (0–100), value, targetValue and shortfallGrams here when ready.

interface NutrientExpandedData {
  value: string;
  /** Minimum target shown as bold in "Target Xg/day" and in "Minimum X" */
  targetValue: string;
  /** 0–100: position of the colored progress fill */
  progressPercent: number;
  /** 0–100: where the minimum threshold marker sits on the bar */
  fillStartPercent?: number;
  isUnderLimit: boolean;
  /** Grams short of minimum (only used when isUnderLimit is true) */
  shortfallGrams?: number;
}

interface NutrientConfig {
  id: string;
  label: string;
  iconSrc: string;
  expandedData: NutrientExpandedData;
  contextualStatus?: ProteinStatus;
}

const PROTEIN_STATUS_BOUNDARY_HOURS = [12, 15, 19];

const getNextProteinStatusBoundary = (now: Date): Date => {
  for (const hour of PROTEIN_STATUS_BOUNDARY_HOURS) {
    const boundary = new Date(now);
    boundary.setHours(hour, 0, 0, 0);
    if (boundary.getTime() > now.getTime()) {
      return boundary;
    }
  }

  const midnight = new Date(now);
  midnight.setDate(midnight.getDate() + 1);
  midnight.setHours(0, 0, 0, 0);
  return midnight;
};

const useProteinStatusTime = (): Date => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const boundary = getNextProteinStatusBoundary(now);
    const delay = Math.max(0, boundary.getTime() - Date.now()) + 100;
    const timeoutId = window.setTimeout(() => setNow(new Date()), delay);

    return () => window.clearTimeout(timeoutId);
  }, [now]);

  return now;
};

const NUTRIENTS: NutrientConfig[] = [
  {
    id: "fibre",
    label: "Fibre",
    iconSrc: "/assets/icons/nutrient-fibre.svg",
    expandedData: {
      value: "24g",
      targetValue: "30g",
      progressPercent: 60,
      isUnderLimit: false,
    },
  },
  {
    id: "sugar",
    label: "Sugar",
    iconSrc: "/assets/icons/nutrient-sugar.svg",
    expandedData: {
      value: "32g",
      targetValue: "50g",
      progressPercent: 45,
      isUnderLimit: false,
    },
  },
];

// ─── Attention icon ───────────────────────────────────────────────────────────

const AttentionIcon = memo(function AttentionIcon({ className }: { className?: string }) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
    >
      <circle cx="12" cy="12" r="9.75" fill="#f9a222" />
      <rect x="11.25" y="7.5" width="1.5" height="5.5" rx="0.75" fill="black" />
      <circle cx="12" cy="15.5" r="0.75" fill="black" />
    </svg>
  );
});

// ─── Progress bar ─────────────────────────────────────────────────────────────

function NutrientProgressBar({
  progressPercent,
  isUnderLimit,
}: {
  progressPercent: number;
  isUnderLimit: boolean;
}) {
  const fillColor = isUnderLimit ? "#f9a222" : "#45ce5d";
  const clampedProgress = Math.max(0, Math.min(100, progressPercent));

  return (
    <div className="relative w-full" style={{ height: 9 }}>
      {/* Track */}
      <div
        className="absolute inset-0 rounded-[12px]"
        style={{ background: "#29175e", border: "1px solid rgba(0,0,0,0.17)" }}
      />
      {/* Current fill */}
      <div
        className="absolute top-0 left-0 h-full rounded-[12px]"
        style={{ width: `${clampedProgress}%`, background: fillColor }}
      />
    </div>
  );
}

// ─── Expanded panel ───────────────────────────────────────────────────────────

function NutrientExpandedPanel({
  data,
  contextualStatus,
}: {
  data: NutrientExpandedData;
  contextualStatus?: ProteinStatus;
}) {
  const shortfallText = `${Math.round(data.shortfallGrams ?? 0)}g`;

  return (
    <div
      className="relative w-full border-b border-[rgba(255,255,255,0.06)]"
      style={{ height: 116, background: "rgba(0,0,0,0.11)" }}
    >
      <div className="absolute flex items-end gap-[34px]" style={{ top: 9, left: 55, right: 12 }}>
        {/* Left column: value / daily average / target */}
        <div className="flex shrink-0 flex-col justify-between" style={{ width: 95, height: 87 }}>
          <p
            style={{
              fontFamily: "var(--font-sans-serif)",
              fontWeight: 600,
              fontSize: 22,
              color: "white",
            }}
          >
            {data.value}
          </p>
          <p
            style={{
              fontFamily: "var(--font-sans-serif)",
              fontWeight: 500,
              fontSize: 13,
              color: "rgba(255,255,255,0.6)",
            }}
          >
            daily average
          </p>
          <p
            style={{
              fontFamily: "var(--font-sans-serif)",
              fontWeight: 500,
              fontSize: 13,
              color: "rgba(255,255,255,0.6)",
            }}
          >
            {"Target "}
            <span style={{ fontWeight: 700, color: "white" }}>{data.targetValue}</span>
            {"/day"}
          </p>
        </div>

        {/* Vertical divider */}
        <div
          className="shrink-0"
          style={{ width: 1, height: 87, background: "rgba(217,217,217,0.11)" }}
        />

        {/* Right column: status text / progress bar / minimum label */}
        <div className="flex flex-1 flex-col justify-between" style={{ height: 79 }}>
          {contextualStatus ? (
            <p
              style={{
                fontFamily: "var(--font-sans-serif)",
                fontWeight: 500,
                fontSize: 13,
                lineHeight: "16px",
                color: "white",
              }}
            >
              {contextualStatus.message}
            </p>
          ) : data.isUnderLimit ? (
            <p
              style={{
                fontFamily: "var(--font-sans-serif)",
                fontWeight: 500,
                fontSize: 13,
                color: "white",
              }}
            >
              {"You're "}
              <span style={{ color: "#f9a222" }}>{shortfallText}</span>
              {" short"}
            </p>
          ) : (
            <p
              style={{
                fontFamily: "var(--font-sans-serif)",
                fontWeight: 500,
                fontSize: 13,
                color: "white",
              }}
            >
              {"You're at "}
              {data.value}
            </p>
          )}
          <NutrientProgressBar
            progressPercent={data.progressPercent}
            isUnderLimit={data.isUnderLimit}
          />
          <p
            style={{
              fontFamily: "var(--font-sans-serif)",
              fontWeight: 400,
              fontSize: 11,
              color: "white",
            }}
          >
            Minimum {data.targetValue}
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── Row ──────────────────────────────────────────────────────────────────────

interface NutrientRowProps {
  nutrient: NutrientConfig;
  isLast: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}

function NutrientRow({ nutrient, isLast, isExpanded }: NutrientRowProps) {
  const { isUnderLimit } = nutrient.expandedData;
  const isAttention = nutrient.contextualStatus
    ? nutrient.contextualStatus.tone === "attention"
    : isUnderLimit;
  const statusLabel =
    nutrient.contextualStatus?.label ?? (isUnderLimit ? "Needs attention" : "On plan");
  const statusColor = isAttention ? "#f9a222" : "#45ce5d";

  return (
    <>
      <button
        type="button"
        // onClick={onToggle} // <- Commented out until we add fibre and sugar
        className={cn(
          "flex h-[56px] w-full items-center justify-between px-[12px]",
          !isExpanded && !isLast && "border-b border-[rgba(255,255,255,0.06)]",
        )}
      >
        <div className="flex items-center gap-[13px]">
          <img src={nutrient.iconSrc} alt="" aria-hidden className="h-[30px] w-[30px] shrink-0" />
          <span
            className="text-white"
            style={{ fontFamily: "var(--font-sans-serif)", fontWeight: 500, fontSize: "16px" }}
          >
            {nutrient.label}
          </span>
        </div>
        <div className="flex items-center gap-[8px]">
          <span
            style={{
              fontFamily: "var(--font-sans-serif)",
              fontWeight: 700,
              fontSize: "14px",
              color: statusColor,
              whiteSpace: "nowrap",
            }}
          >
            {statusLabel}
          </span>
          {isAttention ? (
            <AttentionIcon className="h-[21px] w-[21px]" />
          ) : (
            <PlanStatusCheckIcon className="h-[21px] w-[21px]" bgColour="#45ce5d" />
          )}
        </div>
      </button>

      {/* Animated expand */}
      <div
        className={cn(
          "overflow-hidden transition-all duration-200 ease-out",
          isExpanded ? "max-h-[116px]" : "max-h-0",
        )}
      >
        <NutrientExpandedPanel
          data={nutrient.expandedData}
          contextualStatus={nutrient.contextualStatus}
        />
      </div>
    </>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────────

export const ThisWeekNutritionCard = memo(function ThisWeekNutritionCard() {
  const { proteinBalance, isLoading } = useGetProteinBalance();
  const { plan, isLoading: isPlanLoading } = useGetPlan();
  const now = useProteinStatusTime();

  const [expandedId, setExpandedId] = useState<string | null>("protein");

  const toggle = (id: string) => setExpandedId((prev) => (prev === id ? null : id));

  const loggedProtein = useMemo(
    () => getLoggedProteinSummary(plan, format(now, "yyyy-MM-dd")),
    [now, plan],
  );

  const contextualStatus = useMemo(
    () =>
      getProteinStatus({
        consumedProtein: loggedProtein.consumedProtein,
        targetProtein: proteinBalance?.minimumProtein ?? 0,
        loggedMainMealCount: loggedProtein.loggedMainMealCount,
        localHour: now.getHours() + now.getMinutes() / 60,
      }),
    [loggedProtein, now, proteinBalance?.minimumProtein],
  );

  if (isLoading || isPlanLoading) {
    return (
      <div className="flex flex-col gap-[12px]">
        <IonSkeletonText animated className="h-6 w-44 rounded-lg" />
        <IonSkeletonText animated className="h-[172px] w-full rounded-[18px]" />
      </div>
    );
  }

  const averageProtein = proteinBalance?.averageProtein ?? 0;
  const minimumProtein = proteinBalance?.minimumProtein ?? 0;
  const isUnderLimit = averageProtein < minimumProtein;

  // Bar scale: 0 → 1.5× minimumProtein so minimum sits at ~67% of the bar
  const barMax = minimumProtein * 1.5;
  const progressPercent = barMax ? Math.max(0, Math.min(100, (averageProtein / barMax) * 100)) : 0;
  const fillStartPercent = barMax ? Math.round((minimumProtein / barMax) * 100) : 67;

  const protein: NutrientConfig = {
    id: "protein",
    label: "Protein",
    iconSrc: "/assets/icons/nutrient-protein.svg",
    contextualStatus,
    expandedData: {
      value: `${Math.round(averageProtein)}g`,
      targetValue: `${minimumProtein}g`,
      progressPercent,
      fillStartPercent,
      isUnderLimit,
      shortfallGrams: isUnderLimit ? minimumProtein - averageProtein : undefined,
    },
  };

  // Fibre/sugar rows in NUTRIENTS are still mocked pending server data; only protein renders for now.
  const allNutrients: NutrientConfig[] = [protein, ...NUTRIENTS];

  return (
    <div className="flex flex-col gap-[12px]">
      <h2 className={cn(textVariants.title3, "text-white")}>This week's nutrition</h2>
      <div
        className="overflow-hidden rounded-[18px] border border-[rgba(255,255,255,0.06)]"
      >
        {allNutrients
          .filter((n) => n.id === "protein") // <- temp, only showing protein until we get fibre and sugar from the api
          .map((nutrient, index) => (
            <NutrientRow
              key={nutrient.id}
              nutrient={nutrient}
              isLast={index === allNutrients.length - 1}
              isExpanded={expandedId === nutrient.id}
              onToggle={() => toggle(nutrient.id)}
            />
          ))}
      </div>
    </div>
  );
});
